import { Deduction } from "@/api/deduction"
import { Dispute } from "@/api/dispute"
import { DisputeType } from "@/api/dispute"
import { Option } from "@/utils"
import { DISPUTE_WINDOW_DAYS } from "@/utils/deduction_age"
import { addDays, differenceInDays, parseISO } from "date-fns"

interface DisputeActionError {
  type: "error"
  message: string
  can_redispute: boolean
}

interface RepaymentAction {
  type: "repayment"
  id: string | null
}

interface ActiveDispute {
  type: "active"
  can_redispute: boolean
}

interface CanFile {
  type: "can_file"
  url: Option<string>
  cta: string
}

type DisputeAction = DisputeActionError | RepaymentAction | ActiveDispute | CanFile

/**
 * Days remaining in the dispute filing window (negative = expired), or null when no window
 * is known. Prefers the server-computed `dispute_deadline` (single source of truth in
 * db/src/deduction/dispute_window.rs); payloads without it fall back to the mirror map
 * anchored on the coalesced check date (check_date when present, else invoice_date —
 * matching the server's anchor).
 */
export function days_until_dispute_deadline(deduction: Deduction, now: Date = new Date()): number | null {
  const dispute_deadline = (deduction as Deduction & { dispute_deadline?: string | null }).dispute_deadline
  if (dispute_deadline) {
    return differenceInDays(parseISO(dispute_deadline), now)
  }
  const window = DISPUTE_WINDOW_DAYS[deduction.source]
  if (window == null) return null
  const anchor = new Date(deduction.check_date || deduction.invoice_date)
  return window - differenceInDays(now, anchor)
}

function dollarsToCents(amount: number): number {
  return Math.round(amount * 100)
}

function isFullyRepaid(deduction: Deduction): boolean {
  const repaymentAmount = (deduction as Deduction & { repayment_amount?: number }).repayment_amount
  if (typeof repaymentAmount !== "number") {
    return false
  }

  return dollarsToCents(repaymentAmount) >= dollarsToCents(Math.abs(deduction.invoice_amount))
}

export function getDisputeAction(deduction: Deduction, dispute: Option<Dispute>): Option<DisputeAction> {
  if (deduction.kind === "prepayment") {
    return null
  }
  if (deduction.kind !== "repayment" && isFullyRepaid(deduction)) {
    return null
  }
  if (deduction.kind === "repayment") {
    return {
      type: "repayment",
      id: deduction.repayment_for ?? null,
    }
  }
  // prevents flash on render
  if (deduction.dispute_id && !dispute) {
    return null
  }
  if (dispute && dispute.type !== DisputeType.Backup) {
    return {
      type: "active",
      can_redispute: true,
    }
  }

  // extend windows for super coffee
  let urlParams = new URLSearchParams(window.location.search)
  let extendedWindow = !!urlParams.get("extended")
  // Negative = expired. The extended escape hatch lengthens the window by the extra days
  // beyond the base window (e.g. KeHE 180 -> 720 adds 540 days of slack).
  const days_remaining = days_until_dispute_deadline(deduction)
  if (deduction.source === "cvs") {
    const window = DISPUTE_WINDOW_DAYS.cvs
    if (days_remaining != null && days_remaining < 0) {
      return {
        type: "error",
        message: `Cannot dispute - older than ${window} days`,
        can_redispute: true,
      }
    }
    if (deduction.original_source === "CVS Traverse") {
      return {
        type: "can_file",
        url: `https://cvs.traversesystems.com/#/entry/dispute?keyNum=${deduction.invoice_number}`,
        cta: "File Dispute (CVS)",
      }
    } else {
      return {
        type: "error",
        message: "Cannot dispute - no dispute portal found",
        can_redispute: false,
      }
    }
  } else if (deduction.source === "kehe") {
    const window = extendedWindow ? DISPUTE_WINDOW_DAYS.kehe * 4 : DISPUTE_WINDOW_DAYS.kehe
    const extra_days = window - DISPUTE_WINDOW_DAYS.kehe
    if (days_remaining != null && days_remaining + extra_days < 0) {
      return {
        type: "error",
        message: `Cannot dispute - older than ${window} days`,
        can_redispute: true,
      }
    } else {
      return {
        type: "can_file",
        url: null,
        cta: "File Dispute",
      }
    }
  } else if (deduction.source === "unfi") {
    const window = extendedWindow ? DISPUTE_WINDOW_DAYS.unfi * 2 : DISPUTE_WINDOW_DAYS.unfi
    const extra_days = window - DISPUTE_WINDOW_DAYS.unfi
    if (days_remaining != null && days_remaining + extra_days < 0) {
      return {
        type: "error",
        message: `Cannot dispute - older than ${window} days`,
        can_redispute: true,
      }
    } else {
      return {
        type: "can_file",
        url: null,
        cta: "File Dispute",
      }
    }
  } else {
    // For all other sources, let the backend determine if configuration is needed
    // The FileDisputeDrawer will call check-dispute-capability to determine the actual requirements
    return {
      type: "can_file",
      url: null,
      cta: "File Dispute",
    }
  }
}

export function getCvsDisputeUrl(deduction: Deduction): string {
  return `https://cvs.traversesystems.com/#/entry/dispute?keyNum=${deduction.invoice_number}`
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  // todo: let's make the shield / button yellow if outside the time window but don't need to hide it entirely
  describe("dispute_utils", () => {
    describe("getDisputeAction", () => {
      // @ts-ignore
      const baseDeduction: DeductionResponse = {
        id: "1",
        invoice_number: "12345",
        source: "cvs",
        invoice_date: new Date().toISOString(),
        original_source: "CVS Traverse",
        dispute_id: null,
        pending: false,
        kind: "deduction",
      } as Deduction

      describe("general dispute rules", () => {
        it("should not allow dispute if deduction has dispute_id", () => {
          const deduction = { ...baseDeduction, dispute_id: "123" }
          expect(getDisputeAction(deduction, null)).toEqual(null)
        })

        it("allow dispute if deduction has dispute_id, but the dispute is a backup", () => {
          const deduction = { ...baseDeduction, dispute_id: "123" }
          let dispute = { type: "backup" } as Dispute
          expect(getDisputeAction(deduction, dispute)).toEqual({
            cta: "File Dispute (CVS)",
            type: "can_file",
            url: "https://cvs.traversesystems.com/#/entry/dispute?keyNum=12345",
          })
        })

        it("forbid dispute if deduction has dispute_id, and dispute is chargeback", () => {
          const deduction = { ...baseDeduction, dispute_id: "123" }
          let dispute = { type: "chargeback" } as Dispute
          expect(getDisputeAction(deduction, dispute)).toEqual({
            type: "active",
            can_redispute: true,
          })
        })

        it("should not allow dispute if deduction is prepayment", () => {
          const deduction = { ...baseDeduction, kind: "prepayment" }
          expect(getDisputeAction(deduction, null)).toEqual(null)
        })

        it("should not allow dispute if deduction is fully repaid", () => {
          const deduction = {
            ...baseDeduction,
            source: "kehe",
            invoice_amount: 100,
            repayment_amount: 100,
          } as Deduction
          expect(getDisputeAction(deduction, null)).toEqual(null)
        })

        it("should allow dispute if deduction is partially repaid", () => {
          const deduction = {
            ...baseDeduction,
            source: "kehe",
            invoice_amount: 100,
            repayment_amount: 99.99,
          } as Deduction
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "can_file",
            cta: "File Dispute",
            url: null,
          })
        })

        it("should allow dispute for negative invoice amounts when repayment is partial", () => {
          const deduction = {
            ...baseDeduction,
            source: "kehe",
            invoice_amount: -100,
            repayment_amount: 40,
          } as Deduction
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "can_file",
            cta: "File Dispute",
            url: null,
          })
        })

        it("should allow dispute for unknown distributors", () => {
          const deduction = { ...baseDeduction, source: "unknown" }
          expect(getDisputeAction(deduction, null)).toEqual({
            cta: "File Dispute",
            type: "can_file",
            url: null,
          })
        })
      })

      describe("CVS dispute rules", () => {
        it("should allow dispute for CVS within 45 days with Traverse portal when check_date is present", () => {
          const deduction = {
            ...baseDeduction,
            source: "cvs",
            invoice_date: new Date().toISOString(),
            check_date: new Date().toISOString(),
            original_source: "CVS Traverse",
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "can_file",
            url: "https://cvs.traversesystems.com/#/entry/dispute?keyNum=12345",
            cta: "File Dispute (CVS)",
          })
        })

        it("should allow dispute for CVS within 45 days of invoice_date when check_date is missing", () => {
          const deduction = {
            ...baseDeduction,
            source: "cvs",
            invoice_date: new Date().toISOString(),
            original_source: "CVS Traverse",
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "can_file",
            url: "https://cvs.traversesystems.com/#/entry/dispute?keyNum=12345",
            cta: "File Dispute (CVS)",
          })
        })

        it("should not allow dispute for CVS older than 45 days with check_date", () => {
          const oldDate = addDays(new Date(), -46).toISOString()
          const deduction = {
            ...baseDeduction,
            source: "cvs",
            invoice_date: oldDate,
            check_date: oldDate,
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "error",
            message: "Cannot dispute - older than 45 days",
            can_redispute: true,
          })
        })

        it("should not allow dispute for CVS when invoice_date is older than 45 days and check_date is missing", () => {
          const oldDate = addDays(new Date(), -46).toISOString()
          const deduction = {
            ...baseDeduction,
            source: "cvs",
            invoice_date: oldDate,
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "error",
            message: "Cannot dispute - older than 45 days",
            can_redispute: true,
          })
        })

        it("should allow dispute for CVS when invoice_date is within 45 days and check_date is missing", () => {
          const oldDate = addDays(new Date(), -44).toISOString()
          const deduction = {
            ...baseDeduction,
            source: "cvs",
            invoice_date: oldDate,
            original_source: "CVS Traverse",
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "can_file",
            url: "https://cvs.traversesystems.com/#/entry/dispute?keyNum=12345",
            cta: "File Dispute (CVS)",
          })
        })

        it("should prefer check_date over invoice_date when both exist", () => {
          // Invoice is way past the window, but the check was cut recently — still disputable.
          const deduction = {
            ...baseDeduction,
            source: "cvs",
            invoice_date: addDays(new Date(), -200).toISOString(),
            check_date: new Date().toISOString(),
            original_source: "CVS Traverse",
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "can_file",
            url: "https://cvs.traversesystems.com/#/entry/dispute?keyNum=12345",
            cta: "File Dispute (CVS)",
          })
        })

        it("should not allow dispute for CVS without Traverse portal", () => {
          const deduction = {
            ...baseDeduction,
            source: "cvs",
            original_source: "CVS Other",
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "error",
            message: "Cannot dispute - no dispute portal found",
            can_redispute: false,
          })
        })
      })

      describe("KeHE dispute rules", () => {
        it("should allow dispute for KeHE within 180 days with check_date", () => {
          const deduction = {
            ...baseDeduction,
            source: "kehe",
            invoice_date: new Date().toISOString(),
            check_date: new Date().toISOString(),
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "can_file",
            cta: "File Dispute",
            url: null,
          })
        })

        it("should allow dispute for KeHE within 180 days of invoice_date when check_date is missing", () => {
          const deduction = {
            ...baseDeduction,
            source: "kehe",
            invoice_date: new Date().toISOString(),
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "can_file",
            cta: "File Dispute",
            url: null,
          })
        })

        it("should not allow dispute for KeHE older than 180 days with check_date", () => {
          const oldDate = addDays(new Date(), -181).toISOString()
          const deduction = {
            ...baseDeduction,
            source: "kehe",
            invoice_date: oldDate,
            check_date: oldDate,
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "error",
            message: "Cannot dispute - older than 180 days",
            can_redispute: true,
          })
        })

        it("should not allow dispute for KeHE when invoice_date is older than 180 days and check_date is missing", () => {
          const oldDate = addDays(new Date(), -181).toISOString()
          const deduction = {
            ...baseDeduction,
            source: "kehe",
            invoice_date: oldDate,
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "error",
            message: "Cannot dispute - older than 180 days",
            can_redispute: true,
          })
        })

        it("should allow dispute for KeHE when invoice_date is within 180 days and check_date is missing", () => {
          const oldDate = addDays(new Date(), -179).toISOString()
          const deduction = {
            ...baseDeduction,
            source: "kehe",
            invoice_date: oldDate,
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "can_file",
            cta: "File Dispute",
            url: null,
          })
        })
      })

      describe("server-computed dispute_deadline", () => {
        it("blocks dispute when the server deadline is past, even with a recent invoice date", () => {
          const deduction = {
            ...baseDeduction,
            source: "kehe",
            invoice_date: new Date().toISOString(),
            dispute_deadline: addDays(new Date(), -2).toISOString().slice(0, 10),
          } as Deduction
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "error",
            message: "Cannot dispute - older than 180 days",
            can_redispute: true,
          })
        })

        it("allows dispute while the server deadline is in the future, even with an old invoice date", () => {
          const deduction = {
            ...baseDeduction,
            source: "kehe",
            invoice_date: addDays(new Date(), -400).toISOString(),
            dispute_deadline: addDays(new Date(), 10).toISOString().slice(0, 10),
          } as Deduction
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "can_file",
            cta: "File Dispute",
            url: null,
          })
        })

        it("days_until_dispute_deadline prefers the server deadline and falls back to the mirror map", () => {
          const now = new Date("2026-07-15T12:00:00Z")
          const with_deadline = { ...baseDeduction, source: "kehe", dispute_deadline: "2026-08-14" } as Deduction
          expect(days_until_dispute_deadline(with_deadline, now)).toBe(29)
          const unknown_source = { ...baseDeduction, source: "unknown" } as Deduction
          expect(days_until_dispute_deadline(unknown_source, now)).toBeNull()
        })
      })

      describe("UNFI dispute rules", () => {
        it("should allow dispute for UNFI within 365 days with check_date", () => {
          const deduction = {
            ...baseDeduction,
            source: "unfi",
            invoice_date: new Date().toISOString(),
            check_date: new Date().toISOString(),
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "can_file",
            cta: "File Dispute",
            url: null,
          })
        })

        it("should allow dispute for UNFI within 365 days of invoice_date when check_date is missing", () => {
          const deduction = {
            ...baseDeduction,
            source: "unfi",
            invoice_date: new Date().toISOString(),
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "can_file",
            cta: "File Dispute",
            url: null,
          })
        })

        it("should not allow dispute for UNFI older than 365 days with check_date", () => {
          const oldDate = addDays(new Date(), -366).toISOString()
          const deduction = {
            ...baseDeduction,
            source: "unfi",
            invoice_date: oldDate,
            check_date: oldDate,
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "error",
            message: "Cannot dispute - older than 365 days",
            can_redispute: true,
          })
        })

        it("should not allow dispute for UNFI when invoice_date is older than 365 days and check_date is missing", () => {
          const oldDate = addDays(new Date(), -366).toISOString()
          const deduction = {
            ...baseDeduction,
            source: "unfi",
            invoice_date: oldDate,
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "error",
            message: "Cannot dispute - older than 365 days",
            can_redispute: true,
          })
        })

        it("should allow dispute for UNFI when invoice_date is within 365 days and check_date is missing", () => {
          const oldDate = addDays(new Date(), -364).toISOString()
          const deduction = {
            ...baseDeduction,
            source: "unfi",
            invoice_date: oldDate,
          }
          expect(getDisputeAction(deduction, null)).toEqual({
            type: "can_file",
            cta: "File Dispute",
            url: null,
          })
        })
      })
    })

    describe("getCvsDisputeUrl", () => {
      it("should generate correct CVS dispute URL", () => {
        const deduction = {
          invoice_number: "12345",
        } as Deduction

        expect(getCvsDisputeUrl(deduction)).toBe("https://cvs.traversesystems.com/#/entry/dispute?keyNum=12345")
      })
    })
  })
}
